import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Maximize2, ShieldCheck, Check, Sparkles, ArrowRight } from "lucide-react";
import { ProBadge } from "@/components/ui/ProBadge";
import {
  CANONICAL_PRESETS,
  isPresetPro,
  isPalettePro,
  isFontPro,
  type PresetRecipe,
} from "../design-tokens";
import { COLOR_PALETTES, FONT_PAIRINGS, getPalette, getFontPairing } from "@profolio/templates/catalog";
import { resolveSemanticPalette } from "@profolio/templates/shared";
import { ColorSwatchGrid } from "@/components/ui/visual-controls/ColorSwatchGrid";
import { SegmentedControl } from "@/components/ui/visual-controls/SegmentedControl";
import type { FontPairing, CornerRadius, LayoutDensity } from "@profolio/validators";

import { getStyleRecipe } from "@profolio/templates";

function PresetMiniMockup({ preset }: { preset: PresetRecipe }) {
  const palette = getPalette(preset.config.colorPalette);
  const semantic = resolveSemanticPalette(palette);
  const fonts = getFontPairing(preset.config.fontPairing);
  const isBento = preset.config.sectionArrangement === "bento";
  const isSwiss = preset.id === "porcelain-swiss";
  const isExecutive = preset.id === "obsidian-executive";
  const isCyber = preset.id === "cyber-research";
  const isEditorial = preset.id === "editorial-atelier";
  const isAurora = preset.id === "aurora-showcase";

  const avatarRadiusClass = isSwiss
    ? "rounded-none"
    : isExecutive || isCyber
    ? "rounded-[5px]"
    : isEditorial
    ? "rounded-md"
    : "rounded-full";

  const cardRadiusClass = isSwiss
    ? "rounded-none"
    : isEditorial || isAurora
    ? "rounded-md"
    : "rounded";

  return (
    <div
      className={`w-full h-24 overflow-hidden border p-2 flex flex-col justify-between transition-all select-none ${
        isSwiss ? "rounded-none" : "rounded-lg"
      }`}
      style={{
        backgroundColor: semantic.surface0,
        borderColor: isSwiss ? "#000000" : semantic.borderSubtle,
        color: semantic.textPrimary,
      }}
    >
      <div className="flex items-center gap-1.5">
        <div
          className={`h-5 w-5 flex items-center justify-center text-[8px] font-bold shrink-0 shadow-xs ${avatarRadiusClass}`}
          style={{
            backgroundColor: semantic.accent,
            color: semantic.accentForeground,
            border: isSwiss ? "1px solid #000000" : isCyber ? "1px solid #00f0ff" : undefined,
          }}
        >
          P
        </div>
        <div className="flex-1 space-y-0.5 min-w-0">
          <div
            className={`h-1.5 w-16 ${isSwiss ? "rounded-none" : "rounded-full"}`}
            style={{ backgroundColor: semantic.accent }}
          />
          <div
            className={`h-1 w-24 opacity-60 ${isSwiss ? "rounded-none" : "rounded-full"}`}
            style={{ backgroundColor: semantic.textSecondary }}
          />
        </div>
      </div>

      {isBento ? (
        <div className="grid grid-cols-3 gap-1 my-1">
          <div
            className={`h-7 border p-1 flex flex-col justify-between col-span-2 shadow-2xs ${cardRadiusClass}`}
            style={{
              backgroundColor: semantic.surface1,
              borderColor: isCyber ? "rgba(0, 240, 255, 0.3)" : semantic.borderSubtle,
            }}
          >
            <div
              className={`h-1 w-10 ${isSwiss ? "rounded-none" : "rounded-full"}`}
              style={{ backgroundColor: semantic.accent }}
            />
            <div
              className={`h-1 w-full opacity-40 ${isSwiss ? "rounded-none" : "rounded-full"}`}
              style={{ backgroundColor: semantic.textPrimary }}
            />
          </div>
          <div
            className={`h-7 border p-1 flex flex-col justify-between shadow-2xs ${cardRadiusClass}`}
            style={{
              backgroundColor: semantic.surface2,
              borderColor: isCyber ? "rgba(0, 240, 255, 0.3)" : semantic.borderSubtle,
            }}
          >
            <div
              className={`h-1 w-4 ${isSwiss ? "rounded-none" : "rounded-full"}`}
              style={{ backgroundColor: semantic.accent }}
            />
            <div
              className={`h-1 w-6 opacity-40 ${isSwiss ? "rounded-none" : "rounded-full"}`}
              style={{ backgroundColor: semantic.textPrimary }}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-1 my-1">
          <div
            className={`h-3 border px-1.5 flex items-center justify-between shadow-2xs ${cardRadiusClass}`}
            style={{
              backgroundColor: semantic.surface1,
              borderColor: isSwiss ? "#000000" : semantic.borderSubtle,
            }}
          >
            <div
              className={`h-1 w-12 ${isSwiss ? "rounded-none" : "rounded-full"}`}
              style={{ backgroundColor: semantic.accent }}
            />
            <div
              className={`h-1 w-4 opacity-50 ${isSwiss ? "rounded-none" : "rounded-full"}`}
              style={{ backgroundColor: semantic.accent }}
            />
          </div>
          <div
            className={`h-3 border px-1.5 flex items-center justify-between shadow-2xs ${cardRadiusClass}`}
            style={{
              backgroundColor: semantic.surface1,
              borderColor: isSwiss ? "#000000" : semantic.borderSubtle,
            }}
          >
            <div
              className={`h-1 w-16 opacity-60 ${isSwiss ? "rounded-none" : "rounded-full"}`}
              style={{ backgroundColor: semantic.textPrimary }}
            />
            <div
              className={`h-1 w-3 opacity-40 ${isSwiss ? "rounded-none" : "rounded-full"}`}
              style={{ backgroundColor: semantic.accent }}
            />
          </div>
        </div>
      )}

      <div
        className="flex items-center justify-between text-[9px] pt-1 border-t"
        style={{ borderColor: semantic.borderSubtle }}
      >
        <span
          className={`font-semibold truncate max-w-[120px] ${isEditorial ? "italic" : ""}`}
          style={{ color: semantic.accent }}
        >
          {fonts.name}
        </span>
        <div className="flex items-center gap-1">
          <span
            className={`h-2 w-2 border border-black/10 ${avatarRadiusClass}`}
            style={{ backgroundColor: semantic.accent }}
          />
          <span
            className={`h-2 w-2 border border-black/10 ${cardRadiusClass}`}
            style={{ backgroundColor: semantic.surface2 }}
          />
        </div>
      </div>
    </div>
  );
}

interface GlobalStyleSubViewProps {
  currentDesign: any;
  updateTransientPreview: (updates: any) => void;
  isPro: boolean;
  onUpgradeClick: (featureKey?: string) => void;
  onNavigateToEffects?: () => void;
}

export function GlobalStyleSubView({
  currentDesign,
  updateTransientPreview,
  isPro,
  onUpgradeClick,
  onNavigateToEffects,
}: GlobalStyleSubViewProps) {
  const allPalettes = Object.values(COLOR_PALETTES);
  const allFonts = Object.values(FONT_PAIRINGS);

  const handleSelectPreset = (preset: PresetRecipe) => {
    if (isPresetPro(preset) && !isPro) {
      onUpgradeClick("premium_theme");
      return;
    }
    const currentLayout = currentDesign.layoutOverrides || {};
    const recipe = getStyleRecipe(preset.id);
    const heroDefaults = recipe?.heroDefaults || {};

    updateTransientPreview({
      ...preset.config,
      styleRecipe: preset.id,
      layoutOverrides: {
        ...currentLayout,
        contentMaxWidth: preset.contentMaxWidth ?? currentLayout.contentMaxWidth,
        effects: preset.effects,
        hero: {
          ...currentLayout.hero,
          ...heroDefaults,
        },
      },
    });
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Quick Styles (Presets) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Curated Quick Styles ({CANONICAL_PRESETS.length})
          </h3>
          <span className="text-xs text-muted-foreground">
            Complete harmony of theme, palette, and typography
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {CANONICAL_PRESETS.map((preset) => {
            const Icon = preset.icon;
            const isSelected =
              currentDesign.styleRecipe === preset.id ||
              (!currentDesign.styleRecipe &&
                currentDesign.themeKey === preset.config.themeKey &&
                currentDesign.colorPalette === preset.config.colorPalette &&
                currentDesign.fontPairing === preset.config.fontPairing);
            const locked = isPresetPro(preset) && !isPro;

            return (
              <div
                key={preset.id}
                onClick={() => handleSelectPreset(preset)}
                className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer relative flex flex-col gap-2.5 group ${
                  isSelected
                    ? "border-primary bg-primary/5 ring-1 ring-primary shadow-xs"
                    : "border-border bg-card hover:border-primary/40 hover:shadow-xs"
                }`}
              >
                <PresetMiniMockup preset={preset} />
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="h-7 w-7 rounded-lg bg-muted flex items-center justify-center text-primary shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-sm font-bold text-foreground flex items-center gap-1.5 truncate">
                        <span>{preset.name}</span>
                        {locked && <ProBadge locked />}
                      </h4>
                      <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {preset.description}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                      <Check className="h-3 w-3" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Colors (Palettes) */}
      <div className="space-y-3 border-t border-border pt-6">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Color Palettes ({allPalettes.length})
          </Label>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <ShieldCheck className="h-4 w-4 text-emerald-500" /> WCAG AA Certified
          </span>
        </div>

        <ColorSwatchGrid
          palettes={allPalettes}
          selectedId={currentDesign.colorPalette}
          onSelect={(id) => updateTransientPreview({ colorPalette: id })}
          isProUser={isPro}
          isPalettePro={isPalettePro}
          onUpgradeClick={onUpgradeClick}
        />
      </div>

      {/* Typography (Font Pairings) */}
      <div className="space-y-3 border-t border-border pt-6">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Typography Pairings ({allFonts.length})
          </Label>
          <span className="text-xs text-muted-foreground">Heading & Body font families</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {allFonts.map((font) => {
            const isSelected = currentDesign.fontPairing === font.id;
            const locked = isFontPro(font.id as FontPairing) && !isPro;

            return (
              <button
                key={font.id}
                type="button"
                onClick={() => {
                  if (locked) {
                    onUpgradeClick("premium_font");
                    return;
                  }
                  updateTransientPreview({ fontPairing: font.id });
                }}
                className={`p-3.5 rounded-xl border text-left transition-all outline-none cursor-pointer focus-visible:ring-2 focus-visible:ring-primary ${
                  isSelected
                    ? "border-primary ring-1 ring-primary bg-primary/5 shadow-xs"
                    : "border-border bg-card hover:border-primary/40 hover:bg-muted/40"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-bold text-foreground truncate">{font.name}</span>
                  {locked ? (
                    <ProBadge locked />
                  ) : isSelected ? (
                    <div className="h-4 w-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                      <Check className="h-2.5 w-2.5" />
                    </div>
                  ) : null}
                </div>
                <div className="text-xs text-muted-foreground mb-2">
                  <span>Heading: {font.headingFont}</span> · <span>Body: {font.bodyFont}</span>
                </div>
                <div className="text-xs font-serif italic text-muted-foreground/80 border-t border-border/50 pt-2">
                  "Advancing scientific research through academic excellence."
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Page Width & Spacing */}
      <div className="space-y-4 border-t border-border pt-6">
        <div className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Page Width & Spacing
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Maximize2 className="h-4 w-4 text-muted-foreground" />
              <Label className="text-xs font-semibold">Page Max Width</Label>
            </div>
            <span className="text-xs font-mono font-semibold">
              {currentDesign.layoutOverrides?.contentMaxWidth || 960}px
            </span>
          </div>

          <input
            type="range"
            min={760}
            max={1280}
            step={20}
            value={currentDesign.layoutOverrides?.contentMaxWidth || 960}
            onChange={(e) => {
              const val = parseInt(e.target.value);
              updateTransientPreview({
                layoutOverrides: {
                  ...(currentDesign.layoutOverrides || {}),
                  contentMaxWidth: val,
                },
              });
            }}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />

          <div className="flex justify-between text-xs text-muted-foreground font-mono">
            <button
              type="button"
              onClick={() =>
                updateTransientPreview({
                  layoutOverrides: {
                    ...(currentDesign.layoutOverrides || {}),
                    contentMaxWidth: 760,
                  },
                })
              }
              className="hover:text-foreground cursor-pointer"
            >
              760px (Compact)
            </button>
            <button
              type="button"
              onClick={() =>
                updateTransientPreview({
                  layoutOverrides: {
                    ...(currentDesign.layoutOverrides || {}),
                    contentMaxWidth: 960,
                  },
                })
              }
              className="hover:text-foreground font-semibold text-primary cursor-pointer"
            >
              960px (Standard)
            </button>
            <button
              type="button"
              onClick={() =>
                updateTransientPreview({
                  layoutOverrides: {
                    ...(currentDesign.layoutOverrides || {}),
                    contentMaxWidth: 1280,
                  },
                })
              }
              className="hover:text-foreground cursor-pointer"
            >
              1280px (Expanded)
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Corner Radius</Label>
            <SegmentedControl
              value={currentDesign.cornerRadius || "subtle"}
              onChange={(rad) => updateTransientPreview({ cornerRadius: rad as CornerRadius })}
              options={[
                { value: "none", label: "None (0px)" },
                { value: "subtle", label: "Subtle (8px)" },
                { value: "rounded", label: "Rounded (16px)" },
              ]}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold">Spacing Density</Label>
            <SegmentedControl
              value={currentDesign.layoutDensity || "standard"}
              onChange={(den) => updateTransientPreview({ layoutDensity: den as LayoutDensity })}
              options={[
                { value: "compact", label: "Compact" },
                { value: "standard", label: "Standard" },
                { value: "relaxed", label: "Relaxed" },
              ]}
            />
          </div>
        </div>
      </div>

      {/* ── Screen Edge Curve & Bevel Glass (Direct Quick Control) ── */}
      <div className="space-y-4 border-t border-border pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <Label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Screen Edge Curve & Reflection (2.5D Edge Bevel)
            </Label>
          </div>
          {onNavigateToEffects && (
            <button
              type="button"
              onClick={onNavigateToEffects}
              className="text-xs font-semibold text-primary hover:underline flex items-center gap-1 cursor-pointer"
            >
              <span>Full Effects Studio</span>
              <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="p-4 sm:p-5 rounded-2xl border border-border bg-card shadow-xs space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5 max-w-lg">
              <div className="text-sm font-semibold text-foreground">Curved Glass Edge Refraction</div>
              <div className="text-xs text-muted-foreground leading-relaxed">
                Simulates physical curved glass edge refraction along outer viewport bezels.
              </div>
            </div>
            <Switch
              checked={Boolean(
                currentDesign.layoutOverrides?.pageBevel &&
                currentDesign.layoutOverrides?.pageBevel !== "none"
              )}
              onCheckedChange={(checked) => {
                const currentLayout = currentDesign.layoutOverrides || {};
                const bevelVal = checked ? "medium" : "none";
                updateTransientPreview({
                  layoutOverrides: {
                    ...currentLayout,
                    pageBevel: bevelVal,
                    ...(currentLayout.effects
                      ? { effects: { ...currentLayout.effects, pageBevel: bevelVal } }
                      : {}),
                  },
                });
              }}
            />
          </div>

          {Boolean(
            currentDesign.layoutOverrides?.pageBevel &&
            currentDesign.layoutOverrides?.pageBevel !== "none"
          ) && (
            <div className="pt-3 border-t border-border space-y-4 animate-in fade-in duration-200">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Edge Curve Intensity</Label>
                <SegmentedControl
                  value={
                    currentDesign.layoutOverrides?.pageBevel === "high" ||
                    currentDesign.layoutOverrides?.pageBevel === "mobile-bezel"
                      ? "high"
                      : currentDesign.layoutOverrides?.pageBevel === "medium" ||
                        currentDesign.layoutOverrides?.pageBevel === "curved-glass"
                      ? "medium"
                      : "light"
                  }
                  onChange={(val) => {
                    const currentLayout = currentDesign.layoutOverrides || {};
                    const tierId = val as "light" | "medium" | "high";
                    updateTransientPreview({
                      layoutOverrides: {
                        ...currentLayout,
                        pageBevel: tierId,
                        ...(currentLayout.effects
                          ? { effects: { ...currentLayout.effects, pageBevel: tierId } }
                          : {}),
                      },
                    });
                  }}
                  options={[
                    { value: "light", label: "Subtle (12px)" },
                    { value: "medium", label: "Balanced (18px)" },
                    { value: "high", label: "Pronounced (26px)" },
                  ]}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
